import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { sortItems } from './sort-items';

export const sortGoalsHierarchically = (goals: Goal[], customOrder: ID[]) => {
  if (!goals.length) {
    return [];
  }

  // 1. Identify roots within the provided list
  // A goal is a "root" in this context if its parent is NOT in the list
  const goalIds = new Set(goals.map((g) => g.id));
  const roots: Goal[] = [];
  const childrenMap = new Map<ID, Goal[]>();

  goals.forEach((goal) => {
    const parentId = goal.parentIds?.[goal.parentIds.length - 1];
    const parentExists = parentId && goalIds.has(parentId);

    if (!parentExists) {
      roots.push(goal);
      return;
    }

    // It's a child of someone in the list
    const existing = childrenMap.get(parentId!) ?? [];
    existing.push(goal);
    childrenMap.set(parentId!, existing);
  });

  // 2. Sort the roots by customOrder
  const sortedRoots = sortItems(roots, customOrder, 'id');

  // 3. Flatten recursively, also sorting children by customOrder at each
  // level so the order (including direction reversals applied to customOrder)
  // propagates uniformly through the tree.
  const buildList = (parents: Goal[]): Goal[] => {
    return parents.flatMap((parent) => {
      const children = childrenMap.get(parent.id);
      if (children?.length) {
        const sortedChildren = sortItems(children, customOrder, 'id');
        return [parent, ...buildList(sortedChildren)];
      }
      return [parent];
    });
  };

  return buildList(sortedRoots);
};
